import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import "./RegionPage.css";
import DeviceListModal from "./devices/DeviceListModal";
import { WA_CS_LINK } from "./config/whatsapp";
import { useLanguage } from "./i18n/LanguageContext";
import { dlPushContent } from "./analytics/analytics";

const ICON = (name, size = 14) => (
    <img src={`${process.env.PUBLIC_URL}/icons/${name}.svg`} width={size} height={size} alt="" aria-hidden />
);

function formatRupiah(v) {
    if (!v && v !== 0) return "—";
    return new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(v);
}

const FLAG_OVERRIDES = { hajj2: "sa" };

const getFlagUrl = (code) => {
    if (!code) return null;
    const mapped = FLAG_OVERRIDES[code.toLowerCase()] || (code.length === 2 ? code.toLowerCase() : null);
    return mapped ? `https://flagcdn.com/w80/${mapped}.png` : null;
};

// Copy lives in region.advantages; icons are matched by position.
const KEUNGGULAN_ICONS = ["calendar", "badge-check", "plus-circle"];

// Physical SIM card is offered via Shopee only for the Saudi Arabia region.
const SHOPEE_SIM_FISIK_SA_URL = "https://s.shopee.co.id/AKZ58AyFt3";

const RegionPage = () => {
    const location = useLocation();
    const { regionCode } = useParams();
    const navigate = useNavigate();
    const { t } = useLanguage();

    const [showDeviceModal, setShowDeviceModal] = useState(false);
    const [regionData, setRegionData] = useState(null);
    const [otherDestinations, setOtherDestinations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAllCoverage, setShowAllCoverage] = useState(false);
    const [sortOrder, setSortOrder] = useState("default");
    const [viewMode, setViewMode] = useState("list");
    const [showSortSheet, setShowSortSheet] = useState(false);

    const toNumber = (val) => {
        if (val == null) return 0;
        if (typeof val === "number") return val;
        return Number(String(val).replace(/[^\d.]/g, "")) || 0;
    };

    // Labels live in region.sortOptions.<key>.
    const SORT_OPTIONS = ["default", "price-asc", "price-desc", "data-desc", "data-asc"];

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [regionRes, allRes] = await Promise.all([
                    fetch(`https://api.artamobile.id/v1/product/${regionCode}`),
                    fetch("https://api.artamobile.id/v1/region"),
                ]);
                if (!regionRes.ok) throw new Error("Failed to fetch region data");
                const data = await regionRes.json();

                const normalized = Array.isArray(data.productList)
                    ? data.productList.map((p) => {
                        const gbNum = p.gb != null ? toNumber(p.gb) : toNumber(p.balance);
                        const displayBalance =
                            p.balance && typeof p.balance === "string"
                                ? p.balance
                                : gbNum > 0 ? `${gbNum} GB` : "—";
                        return {
                            ...p,
                            _gbNum: gbNum,
                            _priceNum: toNumber(p.price),
                            _origPriceNum: toNumber(p.originalPrice),
                            _displayBalance: displayBalance,
                        };
                    })
                    : [];

                normalized.sort((a, b) => {
                    if (a._gbNum !== b._gbNum) return a._gbNum - b._gbNum;
                    return a._priceNum - b._priceNum;
                });

                if (!cancelled) {
                    setRegionData({ ...data, productList: normalized });

                    if (allRes.ok) {
                        const allData = await allRes.json();
                        const others = Array.isArray(allData)
                            ? allData
                                .filter((r) => r.regionCode?.toLowerCase() !== regionCode?.toLowerCase() && r.popular !== undefined)
                                .sort((a, b) => (a.popular ?? 99) - (b.popular ?? 99))
                                .slice(0, 2)
                            : [];
                        setOtherDestinations(others);
                    }

                    setLoading(false);
                }
            } catch (err) {
                if (!cancelled) { setError(err); setLoading(false); }
            }
        })();
        return () => { cancelled = true; };
    }, [regionCode]);

    useEffect(() => {
        if (!regionData || loading || error) return;
        const ids = (regionData.productList || []).slice(0, 20).map((p) => p.code).filter(Boolean);
        dlPushContent({
            event: "view_category",
            event_source: "region_page",
            region_code: regionCode,
            region_name: regionData.regionName || "",
            content_type: "product_group",
            content_ids: ids,
        });
    }, [regionData, loading, error, regionCode]);

    const handleSelectProduct = (productCode) => {
        if (!productCode) return;
        navigate(`/product/${productCode}${location.search}`);
    };

    const handleSelectRegion = (code) => {
        navigate(`/region/${code}${location.search}`);
    };

    const { productList = [], regionName = "", regionImage, coverage, networkProvider = [] } = regionData || {};

    const coverageEntries = useMemo(() => {
        const out = [];
        if (!Array.isArray(coverage) || coverage.length === 0) return out;
        for (const chunk of coverage) {
            if (chunk && typeof chunk === "object") {
                for (const rawKey of Object.keys(chunk)) {
                    const country = String(rawKey || "").trim();
                    if (!country) continue;
                    const opsRaw = Array.isArray(chunk[rawKey]) ? chunk[rawKey] : [];
                    const operators = [];
                    for (const item of opsRaw) {
                        if (item && typeof item === "object") {
                            for (const opName of Object.keys(item)) {
                                operators.push({ name: opName, tech: String(item[opName]) });
                            }
                        }
                    }
                    out.push({ country, operators });
                }
            }
        }
        out.sort((a, b) => a.country.localeCompare(b.country, "id"));
        return out;
    }, [coverage]);

    const sortedList = useMemo(() => {
        const list = [...productList];
        switch (sortOrder) {
            case "price-asc":  return list.sort((a, b) => a._priceNum - b._priceNum);
            case "price-desc": return list.sort((a, b) => b._priceNum - a._priceNum);
            case "data-asc":   return list.sort((a, b) => a._gbNum - b._gbNum);
            case "data-desc":  return list.sort((a, b) => b._gbNum - a._gbNum);
            default:           return list;
        }
    }, [productList, sortOrder]);

    const MAX_PREVIEW = 10;
    const shownCoverage = showAllCoverage ? coverageEntries : coverageEntries.slice(0, MAX_PREVIEW);

    if (loading) return <div className="rp-loading">{t("region.loading")}</div>;
    if (error) return <div className="rp-error">{t("region.loadError")} {error.message}</div>;
    if (!regionData) return null;

    const discountPct = (pkg) => {
        if (!pkg._origPriceNum || pkg._origPriceNum <= pkg._priceNum) return null;
        return Math.round((1 - pkg._priceNum / pkg._origPriceNum) * 100);
    };

    // Show the physical-SIM (Shopee) card only for regions that resolve to the SA
    // flag — covers both "hajj2" and a plain "sa" code.
    const isSaudiRegion =
        (FLAG_OVERRIDES[regionCode?.toLowerCase()] || regionCode?.toLowerCase()) === "sa";

    return (
        <div className="rp-page">

            {/* ── Hero: image + TopAppBar + country info overlaid ── */}
            <div className="rp-hero">
                {regionImage && (
                    <img className="rp-hero-img" src={regionImage} alt={regionName} loading="eager" />
                )}
                {/* White-fade gradient at bottom for text readability */}
                <div className="rp-hero-fade" />

                {/* TopAppBar */}
                <div className="rp-topbar">
                    <button className="rp-back-btn" onClick={() => navigate(-1)} aria-label={t("common.back")}>
                        {ICON("arrow-left", 16)}
                    </button>
                    <h1 className="rp-topbar-title">{t("region.topbarTitle", { name: regionName })}</h1>
                    <div style={{ width: 20 }} />
                </div>

                {/* Country info — overlaid starting at y=204 in Figma */}
                <div className="rp-info">
                    {getFlagUrl(regionCode) && (
                        <img
                            className="rp-flag"
                            src={getFlagUrl(regionCode)}
                            alt=""
                            aria-hidden
                            loading="eager"
                        />
                    )}
                    <p className="rp-country-name">{regionName}</p>
                    <p className="rp-country-desc">
                        {t("region.desc", { name: regionName })}
                    </p>
                    <div className="rp-pills">
                        <span className="rp-pill">
                            {ICON("signal-5g", 12)}
                            {t("region.pillSpeed")}
                        </span>
                        <span className="rp-pill">
                            {ICON("hotspot-icon", 12)}
                            {t("region.pillHotspot")}
                        </span>
                    </div>
                </div>
            </div>

            {/* ── Main content — starts 24px below where hero info ends ── */}
            <div className="rp-body">

                {/* eSIM checker banner */}
                <button className="rp-banner" onClick={() => setShowDeviceModal(true)}>
                    <div className="rp-banner-qr-bg" aria-hidden>{ICON("qr-code", 88)}</div>
                    <div className="rp-banner-content">
                        {ICON("phone-device", 24)}
                        <div className="rp-banner-text">
                            <span className="rp-banner-title">{t("region.bannerTitle")}</span>
                            <span className="rp-banner-sub">{t("region.bannerSub")}</span>
                        </div>
                        <span className="rp-banner-cta">{t("region.bannerCta")}</span>
                    </div>
                </button>

                {/* Packages */}
                <div className="rp-packages">
                    <div className="rp-packages-hdr">
                        <p className="rp-packages-title">{t("region.packagesTitle")}</p>
                        <p className="rp-packages-sub">{t("region.packagesSub")}</p>
                    </div>
                    {/* Operator network chips */}
                    {networkProvider.length > 0 && (
                        <div className="rp-operators">
                            {networkProvider.map((op, i) => (
                                <div key={i} className="rp-operator-chip">
                                    <img
                                        src={op.imageUrl}
                                        alt={op.name}
                                        className="rp-operator-logo"
                                        loading="lazy"
                                    />
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Controls row — count + Urutkan + view toggle */}
                    <div className="rp-pkg-controls">
                        <span className="rp-packages-count">{t("common.packages", { n: productList.length })}</span>
                        <div className="rp-pkg-controls-right">
                            {/* Urutkan — Frame 50: 72×24, r=6, pad=(4,8) */}
                            <button className="rp-sort-btn" onClick={() => setShowSortSheet(true)}>
                                <span>{t("common.sort")}</span>
                                {ICON("chevron-down", 14)}
                            </button>
                            {/* View toggle — Frame 51: 56×24, r=6, pad=(4,4) */}
                            <div className="rp-view-toggle">
                                <button
                                    className={`rp-view-btn ${viewMode === "list" ? "active" : ""}`}
                                    onClick={() => setViewMode("list")}
                                    aria-label={t("region.listView")}
                                >
                                    <img src={`${process.env.PUBLIC_URL}/icons/layout-list.svg`} width={16} height={16} alt="" />
                                </button>
                                <div className="rp-view-divider" />
                                <button
                                    className={`rp-view-btn ${viewMode === "grid" ? "active" : ""}`}
                                    onClick={() => setViewMode("grid")}
                                    aria-label={t("region.gridView")}
                                >
                                    <img src={`${process.env.PUBLIC_URL}/icons/layout-grid.svg`} width={16} height={16} alt="" />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className={`rp-pkg-list ${viewMode === "grid" ? "rp-pkg-grid" : ""}`}>
                        {sortedList.map((pkg) => {
                            const pct = discountPct(pkg);
                            return (
                                <div
                                    key={pkg.code}
                                    className="rp-pkg-card"
                                    onClick={() => handleSelectProduct(pkg.code)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => e.key === "Enter" && handleSelectProduct(pkg.code)}
                                >
                                    {/* Badges — tab shape at top-left */}
                                    <div className="rp-pkg-badges">
                                        {pct && <span className="rp-badge rp-badge-promo">{ICON("discount-fire", 10)} {t("common.promo")}</span>}
                                        {pkg.favorite && <span className="rp-badge rp-badge-populer">{ICON("like", 10)} {t("common.popular")}</span>}
                                    </div>

                                    {/* Decorative QR background */}
                                    <div className="rp-pkg-qr-bg" aria-hidden>{ICON("qr-code", 88)}</div>

                                    {/* Main content row */}
                                    <div className="rp-pkg-row">
                                        <div className="rp-pkg-left">
                                            <span className="rp-pkg-data">{pkg._displayBalance}</span>
                                            <div className="rp-pkg-metas">
                                                <span className="rp-pkg-meta">
                                                    <img src={`${process.env.PUBLIC_URL}/clock-icon.png`} width={12} height={12} alt="" aria-hidden />
                                                    {t("common.days", { n: pkg.validity })}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="rp-pkg-right">
                                            <span className="rp-pkg-price">{formatRupiah(pkg._priceNum)}</span>
                                            {pct && (
                                                <div className="rp-pkg-disc-row">
                                                    <span className="rp-pkg-pct">{pct}%</span>
                                                    <span className="rp-pkg-orig">{formatRupiah(pkg._origPriceNum)}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Physical SIM (Shopee) — Saudi Arabia only */}
                    {isSaudiRegion && (
                        <a
                            className="rp-simfisik"
                            href={SHOPEE_SIM_FISIK_SA_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <span className="rp-simfisik-tab">{t("region.simCardTab")}</span>
                            <p className="rp-simfisik-title">{t("region.simCardTitle")}</p>
                            <p className="rp-simfisik-sub">
                                {t("region.simCardSub")}
                            </p>
                            <span className="rp-simfisik-btn">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                    <circle cx="9" cy="21" r="1.6" fill="currentColor" />
                                    <circle cx="18" cy="21" r="1.6" fill="currentColor" />
                                    <path d="M2 3h2.2l2.1 12.2a1.5 1.5 0 0 0 1.5 1.3h9.4a1.5 1.5 0 0 0 1.5-1.2l1.5-7.8H5.2"
                                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                {t("region.simCardCta")}
                            </span>
                        </a>
                    )}
                </div>
            </div>

            {/* ── Keunggulan (full-width green, 390x392) ── */}
            <div className="rp-keunggulan">
                <div className="rp-keunggulan-inner">
                    <div className="rp-keunggulan-hdr">
                        <p className="rp-keunggulan-title">{t("region.advTitle")}</p>
                        <p className="rp-keunggulan-sub">
                            {t("region.advSub", { name: regionName })}
                        </p>
                    </div>
                    {t("region.advantages").map((k, i) => (
                        <div className="rp-keunggulan-card" key={i}>
                            <div className="rp-keunggulan-icon">{ICON(KEUNGGULAN_ICONS[i], 20)}</div>
                            <div className="rp-keunggulan-text">
                                <span className="rp-keunggulan-card-title">{k.title}</span>
                                <span className="rp-keunggulan-card-desc">{k.desc}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="rp-body">
                {/* Pilihan Paket Lainnya */}
                {otherDestinations.length > 0 && (
                    <div className="rp-others">
                        <div className="rp-others-hdr">
                            <p className="rp-others-title">{t("region.otherTitle")}</p>
                            <button className="rp-others-see-all" onClick={() => navigate(`/${location.search}`)}>
                                {t("common.seeAll")}
                            </button>
                        </div>
                        <div className="rp-others-grid">
                            {otherDestinations.map((dest) => (
                                <button
                                    key={dest.regionCode}
                                    className="rp-dest-card"
                                    onClick={() => handleSelectRegion(dest.regionCode)}
                                >
                                    <img
                                        className="rp-dest-img"
                                        src={dest.shopImageUrl}
                                        alt={dest.name}
                                        loading="lazy"
                                        onError={(e) => { e.currentTarget.src = "/fallback-country.png"; }}
                                    />
                                    <div className="rp-dest-info">
                                        <span className="rp-dest-name">{dest.name}</span>
                                        <span className="rp-dest-price">
                                            {t("region.startingFrom", { price: formatRupiah(dest.startingPrice ? Number(String(dest.startingPrice).replace(/[^\d.]/g, "")) * 1000 : 0) })}
                                        </span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Coverage */}
                {coverageEntries.length > 0 && (
                    <div className="rp-coverage">
                        <div className="rp-coverage-hdr">
                            <p className="rp-coverage-title">{t("region.coverageTitle")}</p>
                            <span className="rp-coverage-count">{t("region.coverageCount", { n: coverageEntries.length })}</span>
                        </div>
                        <ul className="rp-coverage-list">
                            {shownCoverage.map(({ country, operators }) => (
                                <li key={country} className="rp-coverage-item">
                                    <span className="rp-coverage-country">{country}</span>
                                    {operators.length > 0 && (
                                        <div className="rp-coverage-ops">
                                            {operators.map((op, idx) => (
                                                <span key={idx} className="rp-coverage-chip">
                                                    {op.name} · {op.tech}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                        {coverageEntries.length > MAX_PREVIEW && (
                            <button className="rp-coverage-toggle" onClick={() => setShowAllCoverage((s) => !s)}>
                                {showAllCoverage ? t("region.showLess") : t("region.showAll")}
                            </button>
                        )}
                    </div>
                )}

                {/* Ada Kendala */}
                <div className="rp-ada-kendala">
                    <div className="rp-ada-kendala-content">
                        <div className="rp-ada-kendala-texts">
                            <p className="rp-ada-kendala-title">{t("help.title")}</p>
                            <p className="rp-ada-kendala-sub">{t("help.desc")}</p>
                        </div>
                        <a
                            className="rp-ada-kendala-btn"
                            href={WA_CS_LINK}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {t("help.cta")}
                        </a>
                    </div>
                    <img
                        className="rp-ada-kendala-illustration"
                        src={`${process.env.PUBLIC_URL}/icons/ada-kendala-illustration.png`}
                        alt=""
                        aria-hidden
                    />
                </div>
            </div>

            {showDeviceModal && <DeviceListModal onClose={() => setShowDeviceModal(false)} />}

            {/* Sort sheet */}
            {showSortSheet && (
                <div className="rp-sort-overlay" onClick={() => setShowSortSheet(false)}>
                    <div className="rp-sort-sheet" onClick={(e) => e.stopPropagation()}>
                        <div className="rp-sort-sheet-handle" />
                        <p className="rp-sort-sheet-title">{t("common.sort")}</p>
                        {SORT_OPTIONS.map((key) => (
                            <button
                                key={key}
                                className={`rp-sort-option ${sortOrder === key ? "active" : ""}`}
                                onClick={() => { setSortOrder(key); setShowSortSheet(false); }}
                            >
                                <span>{t(`region.sortOptions.${key}`)}</span>
                                {sortOrder === key && ICON("check", 16)}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default RegionPage;
